export const ANNOTATION_CODEOWNERS_ENABLED = 'appfolio.com/codeowners-enabled';
export const ANNOTATION_DOMAIN = 'appfolio.com/domain';

/** Group: the team's GetDX reference id. */
export const ANNOTATION_DX_REFERENCE_ID = 'getdx.com/reference-id';

/** Group: entity name of the team's engineering team lead. */
export const ANNOTATION_ENGINEERING_TEAM_LEAD =
  'appfolio.com/engineering-team-lead';

/**
 * Group: additional GitHub teams whose `CODEOWNERS` entries belong to this
 * group, for teams not nested under {@link ANNOTATION_GITHUB_TEAM_SLUG}.
 * Declared as a structured annotation, so its value is a list.
 */
export const ANNOTATION_GITHUB_CODEOWNERS = 'github.com/codeowners';

export const ANNOTATION_GITHUB_LABEL = 'appfolio.com/github-label';

/**
 * Group: whether {@link ANNOTATION_GITHUB_TEAM_SLUG}'s description, members,
 * and IdP group connection are kept in sync by `github-teams-pipeline`.
 * `'true'` or `'false'`; defaults to `'true'` when the declaration omits
 * `github.sync`.
 */
export const ANNOTATION_GITHUB_SYNC = 'appfolio.com/github-sync';

/** Group: the associated GitHub team, as `org/slug` with no `@` prefix. */
export const ANNOTATION_GITHUB_TEAM_SLUG = 'github.com/team-slug';

/** User: GitHub login. Without one, a person cannot be placed on a GitHub team. */
export const ANNOTATION_GITHUB_USER_LOGIN = 'github.com/user-login';

export const ANNOTATION_JIRA_PROJECT_KEY = 'jira/project-key';

/** Group: the key of the team's Jira Product Discovery project. */
export const ANNOTATION_JIRA_JPD_PROJECT_KEY = 'jira/jpd-project-key';

/**
 * Entity refs this entity used to be published under. A structured annotation,
 * so its value is a list.
 *
 * Read by {@link NavigationService}, which builds a redirect from each old ref's
 * catalog and docs URL to this entity, so a link, bookmark, or `spec.owner`
 * written before a rename keeps resolving instead of 404ing.
 */
export const ANNOTATION_MOVED_FROM = 'appfolio.com/moved-from';
export const ANNOTATION_OKTA_ORG_CREATED = 'appfolio.okta.com/created';

/**
 * Group: the Okta group the team is sourced from. Absent on manual groups.
 */
export const ANNOTATION_OKTA_ORG_ID = 'appfolio.okta.com/id';

export const ANNOTATION_OKTA_ORG_LAST_MEMBERSHIP_UPDATED =
  'appfolio.okta.com/last-membership-updated';
export const ANNOTATION_OKTA_ORG_LAST_UPDATED =
  'appfolio.okta.com/last-updated';
/**
 * Group: the group's name exactly as Okta has it, org-naming prefix and all —
 * the un-normalized counterpart to `metadata.title`, which carries the
 * stripped `displayName` instead.
 */
export const ANNOTATION_OKTA_ORG_NAME = 'appfolio.okta.com/name';
/**
 * Read by `plugins/auth-node`'s sign-in resolver, whose `inherit` value decides
 * how far up the group tree a signed-in user's ownership refs walk. Removing it
 * would silently narrow every user's ownership scope rather than failing.
 */
export const ANNOTATION_OWNERSHIP = 'appfolio.com/ownership';
export const ANNOTATION_OWNERSHIP_VALUE_INHERITED = 'inherit';
export const ANNOTATION_PACKAGE_LOCATION = 'appfolio.com/package-location';
export const ANNOTATION_PACKAGE_NAME = 'appfolio.com/package-name';
export const ANNOTATION_PACKAGE_TYPE = 'appfolio.com/package-type';
export const ANNOTATION_PACKAGE_VISIBILITY = 'appfolio.com/package-visibility';

/** Group: the channel to raise anything with the team. */
export const ANNOTATION_SLACK_CHANNEL = 'slack.com/channel';

/** Group: the channel the team expects code review requests in. */
export const ANNOTATION_SLACK_CHANNEL_PULL_REQUEST =
  'slack.com/channel-pull-request';

/** Group: the channel where the security team can post notifications such as CVEs. */
export const ANNOTATION_SLACK_CHANNEL_SECURITY = 'slack.com/channel-security';

export const ANNOTATION_SLACK_ID = 'slack.com/id';
export const ANNOTATION_SLACK_NAME = 'slack.com/name';
export const ANNOTATION_TECHDOCS_ENTITY = 'backstage.io/techdocs-entity';
export const ANNOTATION_TECHDOCS_ENTITY_PATH =
  'backstage.io/techdocs-entity-path';
export const ANNOTATION_TECHDOCS_REF = 'backstage.io/techdocs-ref';
export const ANNOTATION_USER_DEPARTMENT = 'appfolio.com/user-department';
export const ANNOTATION_USER_DIVISION = 'appfolio.com/user-division';

/** User: work email. The join key for most systems outside the portal. */
export const ANNOTATION_USER_ID = 'appfolio.com/user-id';

export const ANNOTATION_USER_LOCATION = 'appfolio.com/user-location';

/** User: the team the person is primarily on, as an entity reference. */
export const ANNOTATION_USER_PRIMARY_GROUP_REF =
  'appfolio.com/user-primary-group-ref';

export const ANNOTATION_USER_ROLE = 'appfolio.com/user-role';
export const ANNOTATION_USER_START_DATE = 'appfolio.com/user-start-date';
export const ANNOTATION_USER_STATUS = 'appfolio.com/user-status';
export const ANNOTATION_USER_TITLE = 'appfolio.com/user-title';
export const DEFAULT_NAMESPACE = 'appfolio';
export const TECHDOCS_SELF_REF = 'dir:.';

/**
 * The system every auto-ingested Claude marketplace and plugin declares
 * membership in. A fixed value, not operator-configurable — a mistyped
 * configuration value would dangle silently. This feature does not create
 * the system entity; membership resolves only once it is authored somewhere
 * the catalog-node provider's own run emits.
 */
export const ENTITY_REF_AI_TOOLING_SYSTEM = 'system:appfolio/ai-tooling';

/**
 * The named relationship pair replacing sub-component containment between a
 * Claude marketplace and its plugins. Both MUST be consumed from this single
 * declaration — a mis-spelled relation type produces no error, just an edge
 * nobody reads. Declared in `catalog-common` rather than `catalog-node` so the
 * names stay reachable from the browser for a future deferred-rendering
 * feature, matching how `deployments-common` holds its deployment pair.
 */
export const RELATION_DISTRIBUTES = 'distributes';
export const RELATION_DISTRIBUTED_BY = 'distributedBy';
